import { useState } from "react";
import { useSelector } from "react-redux";

import { ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItem } from "components/formFields/line/LineInventoryItem";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineReturned } from "components/formFields/line/LineReturned";
import { LineUom } from "components/formFields/line/LineUom";
import { LineVolumeAndVolumeUnit } from "components/formFields/line/LineVolumeAndVolumeUnit";
import { LineWeightAndWeightUnit } from "components/formFields/line/LineWeightAndWeightUnit";

import { getItemRecord } from "services/sosInventoryService/domainLogic";
import { afterTouchLine } from "services/sosInventoryService/moreTransaction/afterTouchLine";
import { updateLineWithItem } from "services/sosInventoryService/moreTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { TYPES } from "services/utility/schema";

import { OBJECT_TYPES, ITEM_QUICKLIST_FORM_TYPES } from "appConstants";
import { LINE_ITEM_FIELDS, EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.RMA.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    line,
    record,
    lineHandler,
    expandItemGroup,
    items,
    itemsCount,
    classes,
    classesCount,
    jobWorkcenters,
    addItem,
    lineItemsErrors: errors,
    lineReadOnly = false,
    handleDeleteLine,
  } = props;
  const { date } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [itemLoading, setItemLoading] = useState(false);

  // SETTINGS
  const showWeightOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnSales
  );
  const showVolumeOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnSales
  );

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = LINE_ITEM_FIELDS[OBJECT_TYPE];
    if (lineItemFields[TYPES.reference].includes(fieldName)) {
      newLine = { ...line, [fieldName]: { id: newValue } };
    } else if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  function handleReferenceChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = {
        ...EMPTY_LINE_ITEM[OBJECT_TYPE],
        lineNumber: line.lineNumber,
      };
      setPageDirty();
      return lineHandler({ type: "update", updatedLine });
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setItemLoading(true);
    const item = await getItemRecord(
      newItem.id,
      "",
      date,
      ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
    );
    const newLine = await updateLineWithItem(item, line, record);
    const updatedLine = afterTouchLine(newLine, "item");
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    setItemLoading(false);
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const updatedLine = afterTouchLine({ ...line, uom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  async function onAddItem(item) {
    setAddNewItem(false);
    await handleItemChange(item);
    addItem();
  }

  function handleDelete() {
    if (handleDeleteLine) {
      handleDeleteLine(line.lineNumber);
    } else {
      setPageDirty();
      lineHandler({ type: "delete", deleteAt: line.lineNumber });
    }
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>
      <LineTableCell sx={{ minWidth: "13rem" }}>
        <LineInventoryItem
          allowExpand
          itemLoading={itemLoading}
          itemFormType={ITEM_QUICKLIST_FORM_TYPES.SALES}
          line={line}
          items={items}
          itemsCount={itemsCount}
          addItem={addItem}
          onValueChange={handleItemChange}
          expandItemGroup={expandItemGroup}
          lineReadOnly={lineReadOnly}
          error={isInError("item")}
        />
      </LineTableCell>
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
          lineReadOnly={lineReadOnly}
        />
      </LineTableCell>
      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={itemLoading}
        lineReadOnly={lineReadOnly}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
        lineReadOnly={lineReadOnly}
      />
      {showWeightOnSales && (
        <LineWeightAndWeightUnit
          weight={line.weight}
          weightunit={line.weightunit}
        />
      )}
      {showVolumeOnSales && (
        <LineVolumeAndVolumeUnit
          volume={line.volume}
          volumeunit={line.volumeunit}
        />
      )}
      <LineReturned
        objectType={OBJECT_TYPE}
        value={line.returned}
        onValueChange={handleValueChange}
        lineReadOnly={lineReadOnly}
      />
      <LineClass
        value={line.class}
        classes={classes}
        classesCount={classesCount}
        onValueChange={handleReferenceChange}
        lineReadOnly={lineReadOnly}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleReferenceChange}
        lineReadOnly={lineReadOnly}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
        />
      )}
    </>
  );
}
